/**
 * Part Overview Drawer Container
 * POV-0040: Implement drawer container component
 * 
 * A right-side sliding drawer for viewing part details with 11 accordion sections.
 * Features: ESC to close, click-outside to close, unsaved changes protection
 */
import React, { useEffect, useCallback, useRef } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../lib/utils';

const PartOverviewDrawer = ({
  isOpen,
  onClose,
  partId,
  children,
  header,
  footer,
  isDirty = false,
  onConfirmClose,
  className,
}) => {
  const drawerRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Handle ESC key to close drawer
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && isOpen) {
      if (isDirty && onConfirmClose) {
        onConfirmClose();
      } else {
        onClose();
      }
    }
  }, [isOpen, isDirty, onClose, onConfirmClose]);

  // Handle click outside drawer
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      if (isDirty && onConfirmClose) {
        onConfirmClose();
      } else {
        onClose();
      }
    }
  }, [isDirty, onClose, onConfirmClose]);

  // Handle close button click
  const handleCloseClick = useCallback(() => {
    if (isDirty && onConfirmClose) {
      onConfirmClose();
    } else {
      onClose();
    }
  }, [isDirty, onClose, onConfirmClose]);

  // Setup keyboard listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Store current active element and focus drawer
      previousActiveElement.current = document.activeElement;
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      // Restore focus when drawer closes
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    };
  }, [isOpen, handleKeyDown]);

  // Focus trap within drawer
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      const focusableElements = drawerRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 transition-opacity duration-200"
        onClick={handleOverlayClick}
        aria-hidden="true"
        data-testid="drawer-overlay"
      />

      {/* Drawer Panel */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
        className={cn(
          "fixed top-0 right-0 h-full bg-white shadow-2xl z-50",
          "flex flex-col",
          "transform transition-transform duration-300 ease-out",
          // Responsive widths per PRD spec
          "w-full md:w-[85vw] lg:w-[50vw]",
          "md:min-w-[520px] md:max-w-[860px]",
          isOpen ? "translate-x-0" : "translate-x-full",
          className
        )}
        data-testid="part-overview-drawer"
      >
        {/* Header - Sticky */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-sm">
          <div className="flex items-start justify-between p-4">
            <div className="flex-1 min-w-0" id="drawer-title">
              {header}
            </div>
            <button
              onClick={handleCloseClick}
              className="ml-4 p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              aria-label="Close drawer"
              data-testid="drawer-close-button"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Dirty state indicator */}
          {isDirty && (
            <div className="px-4 pb-2">
              <div className="flex items-center gap-2 text-amber-600 text-sm">
                <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                Unsaved changes
              </div>
            </div>
          )}
        </div>

        {/* Body - Scrollable */}
        <div 
          className="flex-1 overflow-y-auto"
          data-testid="drawer-body"
        >
          {children}
        </div>

        {/* Footer - Sticky (conditional) */}
        {footer && (
          <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
            {footer}
          </div>
        )}
      </div>
    </>
  );
};

export default PartOverviewDrawer;
